import {
  Alert,
  Button,
  Descriptions,
  Modal,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import type { GetRecordSlotForClientResponse } from '@app/api/Leads/types';
import { handleCatchError } from '@app/services';
import { formatAmount } from '@app/shared/utils/formatAmount';

import type { ColumnsType } from 'antd/es/table';
import type { FC, ReactNode } from 'react';

import './AiRecordSlotModal.scss';

const { Text } = Typography;

interface AiRecordSlotModalProps {
  open: boolean;
  data?: GetRecordSlotForClientResponse;
  isLoading: boolean;
  isCreating: boolean;
  error?: unknown;
  onClose(): void;
  onCreate(): void;
  onCreateManually(): void;
}

// TimeS/TimeE приходят в часовом поясе филиала, поэтому показываем их как есть,
// без перевода в локальную зону оператора.
const DATE_FORMAT = 'DD.MM.YYYY';
const TIME_FORMAT = 'HH:mm';

const LOADING_TIP = 'Анализ возможности создания записи';

const getClientName = (
  nameFirst?: string | null,
  nameLast?: string | null
): string => {
  const clientName = [nameLast, nameFirst]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

  return clientName || '—';
};

const formatSlotTime = (timeS: string, timeE: string): string => {
  const start = dayjs(timeS);
  const end = dayjs(timeE);

  if (!start.isValid()) return '—';

  const startText = `${start.format(DATE_FORMAT)}, ${start.format(
    TIME_FORMAT
  )}`;

  return end.isValid()
    ? `${startText} – ${end.format(TIME_FORMAT)}`
    : startText;
};

type ServiceRow = {
  key: number;
  name: string;
  count: number;
  duration: number;
  price: number;
};

const SERVICE_COLUMNS: ColumnsType<ServiceRow> = [
  {
    title: 'Услуга',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Кол-во',
    dataIndex: 'count',
    key: 'count',
    width: 80,
    align: 'center',
  },
  {
    title: 'Длит., мин',
    dataIndex: 'duration',
    key: 'duration',
    width: 100,
    align: 'center',
  },
  {
    title: 'Стоимость',
    dataIndex: 'price',
    key: 'price',
    width: 120,
    align: 'right',
    render: (price: number) => formatAmount(price),
  },
];

export const AiRecordSlotModal: FC<AiRecordSlotModalProps> = ({
  open,
  data,
  isLoading,
  isCreating,
  error,
  onClose,
  onCreate,
  onCreateManually,
}) => {
  const serviceRows = useMemo<ServiceRow[]>(
    () =>
      (data?.Services ?? []).map((service) => ({
        key: service.Id,
        name: service.Name ?? '—',
        count: service.Count,
        duration: service.DurationMins,
        price: service.Price,
      })),
    [data?.Services]
  );

  const totalPrice = useMemo(
    () => serviceRows.reduce((sum, { price, count }) => sum + price * count, 0),
    [serviceRows]
  );

  const canCreateRecord = data?.CanCreateRecord === true;
  // Без телефона автоматически создать запись нельзя (подтверждение записи
  // клиенту и т.п.), поэтому смотрим на Phone1 отдельно от CanCreateRecord.
  const hasPhone = Boolean(data?.Phone1?.trim());
  // Слот подобран — показываем карточку с деталями (клиент, филиал, время,
  // услуги), даже если подтвердить создание нельзя из-за отсутствия телефона.
  const isSlotReady = Boolean(data) && !error && canCreateRecord;
  const canConfirmCreate = isSlotReady && hasPhone;
  // ИИ не смог подобрать слот вовсе — предлагаем ручной сценарий записи.
  const noSlotAvailable = Boolean(data) && !error && !canCreateRecord;
  // Слот есть, но не хватает телефона для автосоздания — тоже ручной сценарий.
  const shouldOfferManual = noSlotAvailable || (isSlotReady && !hasPhone);

  const footerButtons: ReactNode[] = [
    <Button key="cancel" disabled={isCreating} onClick={onClose}>
      Отменить
    </Button>,
  ];

  if (canConfirmCreate) {
    footerButtons.push(
      <Button
        key="create"
        type="primary"
        loading={isCreating}
        onClick={onCreate}
      >
        Создать
      </Button>
    );
  }

  if (shouldOfferManual) {
    footerButtons.push(
      <Button key="manual" type="primary" onClick={onCreateManually}>
        Создать запись вручную
      </Button>
    );
  }

  return (
    <Modal
      className="AiRecordSlotModal"
      title="Запись клиента с помощью ИИ"
      open={open}
      onCancel={onClose}
      footer={footerButtons}
      destroyOnHidden
    >
      <Spin spinning={isLoading} tip={LOADING_TIP}>
        {error ? (
          <Alert
            type="error"
            showIcon
            message="Не удалось подобрать слот для записи"
            description={handleCatchError(error)}
          />
        ) : noSlotAvailable ? (
          <Alert
            type="info"
            showIcon
            message="ИИ не смог подобрать подходящий слот для записи"
            description="Вы можете создать запись вручную."
          />
        ) : isSlotReady && data ? (
          <div className="AiRecordSlotModal__content">
            {!hasPhone && (
              <Alert
                type="warning"
                showIcon
                message="У клиента не указан номер телефона"
                description="Автоматическое создание записи недоступно. Вы можете создать запись вручную."
              />
            )}

            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Клиент">
                {getClientName(data.NameFirst, data.NameLast)}
              </Descriptions.Item>
              <Descriptions.Item label="Телефон">
                <span className="AiRecordSlotModal__phone">
                  <Text>{data.Phone1 ?? '—'}</Text>
                  <Tag color={data.IsPhoneVerified ? 'green' : 'default'}>
                    {data.IsPhoneVerified ? 'Подтверждён' : 'Не подтверждён'}
                  </Tag>
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Филиал">
                {data.BranchName ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Мастер">
                {data.EmployeeName ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Дата и время">
                {formatSlotTime(data.TimeS, data.TimeE)}
              </Descriptions.Item>
            </Descriptions>

            <Table
              className="AiRecordSlotModal__services"
              size="small"
              pagination={false}
              columns={SERVICE_COLUMNS}
              dataSource={serviceRows}
              locale={{ emptyText: 'Услуги не подобраны' }}
              summary={() =>
                serviceRows.length > 0 ? (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text strong>Итого</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong>{formatAmount(totalPrice)}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                ) : null
              }
            />
          </div>
        ) : isLoading ? (
          <div className="AiRecordSlotModal__placeholder" />
        ) : (
          <Text type="secondary">Нет данных для отображения</Text>
        )}
      </Spin>
    </Modal>
  );
};
